import CheckboxUntyped from "@components/base/checkbox/checkbox";
import { useEffect, useRef } from "react";
import type { ReactElement } from "react";
import { HIGH_CONFIDENCE_THRESHOLD, POTENTIAL_MATCH_THRESHOLD } from "../constants";
import { countActiveFilters, DEFAULT_RESULT_FILTERS, toggleFilterValue } from "../utils";
import type { MatchStatus, ResultFilters, ReviewState } from "../types";

const Checkbox = CheckboxUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

const CONFIDENCE_OPTIONS: { key: MatchStatus; label: string; range: string }[] = [
  { key: "HIGH_CONFIDENCE", label: "High Confidence", range: `${HIGH_CONFIDENCE_THRESHOLD}–100%` },
  { key: "POTENTIAL_MATCH", label: "Potential Match", range: `${POTENTIAL_MATCH_THRESHOLD}–${HIGH_CONFIDENCE_THRESHOLD - 1}%` },
  { key: "LOW_CONFIDENCE", label: "Low Confidence", range: `1–${POTENTIAL_MATCH_THRESHOLD - 1}%` },
  { key: "NO_MATCH", label: "No Match", range: "0%" },
];

const REVIEW_OPTIONS: { key: ReviewState; label: string }[] = [
  { key: "NEEDS_REVIEW", label: "Need Review" },
  { key: "REVIEWED", label: "Reviewed" },
  { key: "NO_REVIEW_REQUIRED", label: "No Review Required" },
];

type ResultsFiltersProps = {
  filters: ResultFilters;
  onChange: (filters: ResultFilters) => void;
  onClose: () => void;
};

const ResultsFilters = ({ filters, onChange, onClose }: ResultsFiltersProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const activeCount = countActiveFilters(filters);

  return (
    <div ref={containerRef} className="tw-absolute tw-right-0 tw-top-full tw-z-50 tw-mt-2 tw-w-[300px] tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-autoSuggest">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-4 tw-py-3">
        <span className="tw-text-sm tw-font-semibold tw-text-gray-900">Filters</span>
        <button type="button" disabled={!activeCount} onClick={() => onChange(DEFAULT_RESULT_FILTERS)} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-600 disabled:tw-text-gray-400">
          Clear all
        </button>
      </div>

      <div className="tw-px-4 tw-py-3">
        <div className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-gray-500">Confidence</div>
        {CONFIDENCE_OPTIONS.map((option) => (
          <div key={option.key} className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-sm tw-text-gray-900">
            <Checkbox id={`confidence-${option.key}`} checked={filters.confidence.includes(option.key)} onChange={() => onChange({ ...filters, confidence: toggleFilterValue(filters.confidence, option.key) })} label={option.label} />
            <span className="tw-text-xs tw-text-gray-500">{option.range}</span>
          </div>
        ))}
      </div>

      <div className="tw-border-t tw-border-gray-200 tw-px-4 tw-py-3">
        <div className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-gray-500">Matched against</div>
        {REVIEW_OPTIONS.map((option) => (
          <div key={option.key} className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-900">
            <Checkbox id={`review-${option.key}`} checked={filters.review.includes(option.key)} onChange={() => onChange({ ...filters, review: toggleFilterValue(filters.review, option.key) })} label={option.label} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResultsFilters;
